import type { ReactNode } from 'react';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import { TimePicker } from '@/components/ui/time-picker';
import {
  dateToSliderValue,
  SHARED_TIME_SLIDER_MAX,
} from './utils/useSharedTnGuardLayerTimeRange';

export type LayerTimeRangeControlProps = {
  start: Date;
  end: Date;
  fullStart: Date;
  fullEnd: Date;
  onStartChange: (date: Date) => void;
  onEndChange: (date: Date) => void;
  onRangeFromSlider: (startValue: number, endValue: number) => void;
  title?: string;
  subtitle?: string;
  /**
   * Prefix for slider input ids (avoid duplicate ids when multiple controls
   * mount).
   */
  idPrefix?: string;
  /**
   * When false, hide the muted full-extent bound labels above the selected
   * range.
   */
  showFullExtentLabels?: boolean;
};

function formatStepLabel(date: Date | null | undefined): string {
  if (!date) return '—';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatTimeHHmm(date: Date): string {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

function clampDate(value: Date, min: Date, max: Date): Date {
  if (value.getTime() < min.getTime()) return new Date(min);
  if (value.getTime() > max.getTime()) return new Date(max);
  return value;
}

function applyDateKeepingTime(current: Date, nextDate: Date): Date {
  const next = new Date(nextDate);
  next.setHours(current.getHours(), current.getMinutes(), 0, 0);
  return next;
}

function applyTimeKeepingDate(current: Date, timeHHmm: string): Date {
  const [hoursRaw, minutesRaw] = timeHHmm.split(':');
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return current;
  }
  const next = new Date(current);
  next.setHours(hours, minutes, 0, 0);
  return next;
}

export function LayerTimeRangeControlShell({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <div className="layer-time-range-control tw:relative tw:z-[1] tw:overflow-visible tw:rounded-md tw:border tw:border-border tw:bg-muted/40 tw:px-3 tw:py-2 tw:text-foreground">
      {children}
    </div>
  );
}

export function LayerTimeRangeControlMessage({ message }: { message: string }) {
  return (
    <LayerTimeRangeControlShell>
      <span className="tw:text-xs tw:text-muted-foreground">{message}</span>
    </LayerTimeRangeControlShell>
  );
}

/**
 * Presentational start/stop datetime pickers + dual range sliders. Shared by
 * the Layers tab TN Guard control and Data Sources Filter by Datetime.
 */
export function LayerTimeRangeControl({
  start,
  end,
  fullStart,
  fullEnd,
  onStartChange,
  onEndChange,
  onRangeFromSlider,
  title,
  subtitle,
  idPrefix = 'layer-time',
  showFullExtentLabels = true,
}: LayerTimeRangeControlProps) {
  const startSlider = dateToSliderValue(start, fullStart, fullEnd);
  const endSlider = dateToSliderValue(end, fullStart, fullEnd);
  const startId = `${idPrefix}-start`;
  const endId = `${idPrefix}-end`;
  const startDateId = `${idPrefix}-start-date`;
  const startTimeId = `${idPrefix}-start-time`;
  const endDateId = `${idPrefix}-end-date`;
  const endTimeId = `${idPrefix}-end-time`;

  return (
    <LayerTimeRangeControlShell>
      {title ? (
        <div className="tw:mb-2 tw:text-xs tw:font-medium">{title}</div>
      ) : null}
      {subtitle ? (
        <div className="tw:mb-3 tw:text-xs tw:text-muted-foreground">
          {subtitle}
        </div>
      ) : null}

      <div className="tw:mb-3 tw:flex tw:flex-col tw:gap-3">
        <div className="tw:flex tw:flex-col tw:gap-1.5">
          <Label className="tw:text-xs">Start</Label>
          <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:sm:grid-cols-2">
            <DatePicker
              id={startDateId}
              value={start}
              minDate={fullStart}
              maxDate={end}
              aria-label="Start date"
              onChange={(date) => {
                if (!date) return;
                onStartChange(
                  clampDate(applyDateKeepingTime(start, date), fullStart, end)
                );
              }}
            />
            <TimePicker
              id={startTimeId}
              value={formatTimeHHmm(start)}
              aria-label="Start time"
              onChange={(time) => {
                onStartChange(
                  clampDate(applyTimeKeepingDate(start, time), fullStart, end)
                );
              }}
            />
          </div>
        </div>
        <div className="tw:flex tw:flex-col tw:gap-1.5">
          <Label className="tw:text-xs">Stop</Label>
          <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:sm:grid-cols-2">
            <DatePicker
              id={endDateId}
              value={end}
              minDate={start}
              maxDate={fullEnd}
              aria-label="Stop date"
              onChange={(date) => {
                if (!date) return;
                onEndChange(
                  clampDate(applyDateKeepingTime(end, date), start, fullEnd)
                );
              }}
            />
            <TimePicker
              id={endTimeId}
              value={formatTimeHHmm(end)}
              aria-label="Stop time"
              onChange={(time) => {
                onEndChange(
                  clampDate(applyTimeKeepingDate(end, time), start, fullEnd)
                );
              }}
            />
          </div>
        </div>
      </div>

      {showFullExtentLabels ? (
        <div className="tw:mb-1 tw:flex tw:items-center tw:justify-between tw:gap-2">
          <span className="tw:text-xs tw:text-muted-foreground">
            {formatStepLabel(fullStart)}
          </span>
          <span className="tw:text-xs tw:text-muted-foreground">
            {formatStepLabel(fullEnd)}
          </span>
        </div>
      ) : null}
      <div className="tw:mb-2 tw:flex tw:items-center tw:justify-between tw:gap-2">
        <span className="tw:text-xs tw:text-foreground">
          {formatStepLabel(start)}
        </span>
        <span className="tw:text-end tw:text-xs tw:text-foreground">
          {formatStepLabel(end)}
        </span>
      </div>

      <label
        className="tw:mb-1 tw:block tw:text-xs tw:font-medium"
        htmlFor={startId}
      >
        Start
      </label>
      <input
        id={startId}
        type="range"
        className="form-range"
        min={0}
        max={SHARED_TIME_SLIDER_MAX}
        step={1}
        value={startSlider}
        onChange={(e) => {
          const nextStart = Number(e.target.value);
          onRangeFromSlider(nextStart, Math.max(nextStart, endSlider));
        }}
        aria-label="Filter start datetime"
      />
      <label
        className="tw:mb-1 tw:block tw:text-xs tw:font-medium"
        htmlFor={endId}
      >
        Stop
      </label>
      <input
        id={endId}
        type="range"
        className="form-range"
        min={0}
        max={SHARED_TIME_SLIDER_MAX}
        step={1}
        value={endSlider}
        onChange={(e) => {
          const nextEnd = Number(e.target.value);
          onRangeFromSlider(Math.min(startSlider, nextEnd), nextEnd);
        }}
        aria-label="Filter stop datetime"
      />
    </LayerTimeRangeControlShell>
  );
}
